/**
 *
 *   start(areaId) → poll task status every N ms → on SUCCESS handle result.
 *
 * GraphQL access and timers are injected, which keeps
 * polling strictly serial (no overlapping polls) and cancellable.
 */

import {
  AI_MAPPING_TASK_MAX_POLLS,
  AI_MAPPING_TASK_POLL_INTERVAL_MS,
  AiMappingTaskSnapshot,
  AiMappingTaskStatusLike,
  initialAiMappingTaskSnapshot,
  reduceAiMappingTaskStatus,
} from "./aiMappingTask";

export interface AiMappingDetectFlowDeps<TResults = unknown> {
  startTask: (areaId: string) => Promise<{ taskId: string }>;
  fetchTaskStatus: (taskId: string) => Promise<AiMappingTaskStatusLike>;
  fetchResults?: (areaId: string) => Promise<TResults>;
  sleep?: (ms: number) => Promise<void>;
  pollIntervalMs?: number;
  maxPolls?: number;
  isCancelled?: () => boolean;
  onSnapshot?: (snapshot: AiMappingTaskSnapshot) => void;
}

export type AiMappingDetectFlowOutcome<TResults = unknown> =
  | { kind: "success"; results?: TResults; pdfUrl: string | null }
  | { kind: "failure"; error: string }
  | { kind: "cancelled" };

const defaultSleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export async function runAiMappingDetectFlow<TResults>(
  areaId: string,
  deps: AiMappingDetectFlowDeps<TResults>
): Promise<AiMappingDetectFlowOutcome<TResults>> {
  const sleep = deps.sleep ?? defaultSleep;
  const pollIntervalMs = deps.pollIntervalMs ?? AI_MAPPING_TASK_POLL_INTERVAL_MS;
  const maxPolls = deps.maxPolls ?? AI_MAPPING_TASK_MAX_POLLS;
  const isCancelled = deps.isCancelled ?? (() => false);
  const emit = deps.onSnapshot ?? (() => {});

  let snapshot = initialAiMappingTaskSnapshot;
  emit(snapshot);

  const { taskId } = await deps.startTask(areaId);

  for (let poll = 0; poll < maxPolls; poll += 1) {
    if (isCancelled()) return { kind: "cancelled" };

    // Await each poll fully before scheduling the next — polls never overlap.
    const status = await deps.fetchTaskStatus(taskId);
    snapshot = reduceAiMappingTaskStatus(snapshot, status);
    emit(snapshot);

    if (snapshot.phase === "success") {
      if (isCancelled()) return { kind: "cancelled" };
      if (deps.fetchResults) {
        const results = await deps.fetchResults(areaId);
        if (isCancelled()) return { kind: "cancelled" };
        return { kind: "success", results, pdfUrl: snapshot.pdfUrl };
      }
      return { kind: "success", pdfUrl: snapshot.pdfUrl };
    }

    if (snapshot.phase === "failure") {
      return { kind: "failure", error: snapshot.error ?? "Task failed" };
    }

    await sleep(pollIntervalMs);
  }

  return { kind: "failure", error: "Task timed out. Please try again." };
}
